import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import ReminderEmailSettings from '../components/ReminderEmailSettings';
import ScormEmailSetup from '../components/ScormEmailSetup';
import { SHOW_PER_COURSE_REMINDER_SETTINGS } from '../constants/studentReminderPreferences';
import { courseAPI, reminderPreferencesAPI } from '../services/api';
import { BackIcon } from '../utils/icons';
import { getLocalizedText } from '../utils/i18n';
import '../styles/components/_reminder-emails.scss';

const sanitizeReturnTo = (raw: string | null): string => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return '/';
  }
  return raw;
};

const ReminderEmailPreferencesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnTo = sanitizeReturnTo(searchParams.get('returnTo'));
  const courseId = SHOW_PER_COURSE_REMINDER_SETTINGS
    ? searchParams.get('courseId')?.trim() || ''
    : '';

  const [loading, setLoading] = useState(true);
  const [studentEmail, setStudentEmail] = useState<string | null>(null);
  const [emailEditable, setEmailEditable] = useState(false);
  const [courseTitle, setCourseTitle] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const prefs = await reminderPreferencesAPI.getGlobal();
      setStudentEmail(prefs.email);
      setEmailEditable(prefs.emailEditable);

      if (courseId) {
        try {
          const course = await courseAPI.getCourse(courseId);
          setCourseTitle(getLocalizedText(course.title));
        } catch {
          setCourseTitle(null);
        }
      } else {
        setCourseTitle(null);
      }
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleBack = () => {
    navigate(returnTo);
  };

  const handleEmailSaved = (email: string) => {
    setStudentEmail(email);
    setEmailEditable(false);
    try {
      const raw = localStorage.getItem('student_user');
      if (raw) {
        const user = JSON.parse(raw) as { email?: string };
        localStorage.setItem('student_user', JSON.stringify({ ...user, email }));
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <AppLayout>
      <Box className="reminder-emails__page">
        <Button
          startIcon={<BackIcon size={18} />}
          onClick={handleBack}
          className="reminder-emails__back-button"
        >
          {t('reminderEmails.back')}
        </Button>

        <Box className="reminder-emails__page-header">
          <h1 className="reminder-emails__page-title">
            {t('reminderEmails.pageTitle')}
          </h1>
          <p className="reminder-emails__page-description">
            {t('reminderEmails.pageDescription')}
          </p>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box className="reminder-emails__panel">
            {emailEditable && (
              <ScormEmailSetup initialEmail={studentEmail} onSaved={handleEmailSaved} />
            )}

            {!emailEditable && studentEmail && (
              <Typography variant="body2" className="reminder-emails__saved-email">
                {studentEmail}
              </Typography>
            )}

            <ReminderEmailSettings variant="global" />

            {SHOW_PER_COURSE_REMINDER_SETTINGS && courseId && (
              <>
                <Divider className="reminder-emails__divider" />
                <Typography variant="subtitle2" className="reminder-emails__course-heading">
                  {courseTitle || t('reminderEmails.courseSectionFallback')}
                </Typography>
                <ReminderEmailSettings variant="course" courseId={courseId} />
              </>
            )}
          </Box>
        )}
      </Box>
    </AppLayout>
  );
};

export default ReminderEmailPreferencesPage;
